
'use client';

import * as React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { CheckCircle, Clock, Gift, Loader2, Lock, PlusCircle, QrCode, Star, Target, BarChart3, Trash2, Zap, Trophy, ArrowLeft, AlertCircle } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth-context';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger, DialogClose } from './ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Tooltip, Bar } from 'recharts';
import { useRouter } from 'next/navigation';
import AuthGate from './auth-gate';
import { db } from '@/lib/firebase';
import { doc, setDoc, onSnapshot, collection, addDoc, deleteDoc, writeBatch, serverTimestamp, getDocs, query, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import type { Habit, HabitLog, UserHabitData } from '@/lib/types';
import ThemeSelector from './theme-selector';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import Image from 'next/image';
import { Progress } from './ui/progress';
import { addDays, format, isBefore } from 'date-fns';

const predefinedHabits: Omit<Habit, 'id' | 'completed' | 'isCustom'>[] = [
    { name: 'Pray 5 Times Daily', description: 'Log each of your five daily prayers.' },
    { name: 'Read Quran', description: 'Recite a portion of the Quran.' },
    { name: 'Do Dhikr', description: 'Engage in remembrance of Allah.' },
];

const PremiumFeature = ({ icon: Icon, title, description, comingSoon = false }: { icon: React.ElementType, title: string, description: string, comingSoon?: boolean }) => (
    <li className="flex items-start gap-4">
        <div className="flex-shrink-0 bg-primary/20 text-primary rounded-full p-2">
            <Icon className="w-5 h-5" />
        </div>
        <div>
            <div className="flex items-center gap-2">
                 <h3 className="font-semibold">{title}</h3>
                 {comingSoon && <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Soon</span>}
            </div>
            <p className="text-sm text-muted-foreground">{description}</p>
        </div>
    </li>
);

const createHabitSchema = z.object({
    name: z.string().min(3, "Habit name must be at least 3 characters.").max(50, "Habit name is too long."),
    description: z.string().max(100, "Description is too long.").optional(),
});


const HabitDashboard = ({ habitLogs }: { habitLogs: HabitLog[] }) => {
    const data = React.useMemo(() => {
        const last30Days = Array.from({ length: 30 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - i);
            return date.toISOString().split('T')[0];
        }).reverse();

        return last30Days.map(dateStr => {
            const log = habitLogs.find(l => l.date === dateStr);
            const completionPercentage = log ? (log.completedCount / log.totalCount) * 100 : 0;
            return {
                date: new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                completion: completionPercentage,
            };
        });
    }, [habitLogs]);

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Progress Dashboard</CardTitle>
                <CardDescription>Your habit completion rate over the last 30 days.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <XAxis dataKey="date" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis fontSize={12} tickLine={false} axisLine={false} unit="%" />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'hsl(var(--background))',
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: 'var(--radius)',
                                }}
                                labelStyle={{ color: 'hsl(var(--foreground))' }}
                                 formatter={(value: number) => [`${value.toFixed(0)}%`, 'Completion']}
                            />
                            <Bar dataKey="completion" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
};

const RewardCard = ({ onClaim, rewardClaimedAt, rewardStreakGoal }: { onClaim: () => Promise<void>; rewardClaimedAt: Timestamp | null, rewardStreakGoal: number }) => {
    const [loading, setLoading] = React.useState(false);
    
    const handleClaim = async () => {
        setLoading(true);
        await onClaim();
        // The parent component's state will update and re-render this card.
        setLoading(false);
    };

    if (rewardClaimedAt) {
        const claimDate = rewardClaimedAt.toDate();
        const expiryDate = addDays(claimDate, 7);
        const isExpired = isBefore(expiryDate, new Date());

        if (isExpired) {
            return (
                <Card className="bg-muted border-dashed border-muted-foreground overflow-hidden">
                    <CardHeader className="text-center">
                        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground" />
                        <CardTitle className="text-2xl font-bold">Reward Expired</CardTitle>
                    </CardHeader>
                    <CardContent className="text-center space-y-4">
                        <p className="text-muted-foreground">
                            Your reward code expired on {format(expiryDate, 'dd MMM yyyy')}.
                            <br />
                            Keep up your streaks for future rewards!
                        </p>
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card className="bg-gradient-to-br from-yellow-400/20 via-amber-500/20 to-orange-500/20 border-amber-500/50 overflow-hidden">
                <CardHeader className="text-center">
                    <Trophy className="w-12 h-12 mx-auto text-amber-500" />
                    <CardTitle className="text-2xl font-bold">Reward Unlocked!</CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-4 animate-fade-in">
                    <h3 className="text-lg font-semibold">Your Smashhh Burgers Reward</h3>
                    <div className="bg-white p-6 rounded-lg inline-block shadow-md border-2 border-dashed border-primary">
                        <p className="text-2xl font-bold text-primary tracking-widest">SMASHNAV10</p>
                    </div>
                    <p className="text-muted-foreground">
                        Use this code at any Smashhh Burgers location to redeem your reward:
                        <br />
                        <strong className="text-foreground">10% off your total bill OR a free add-on</strong>.
                    </p>
                    <p className="text-sm text-red-600 font-semibold">
                        Expires: {format(expiryDate, 'dd MMM yyyy, h:mm a')}
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="bg-gradient-to-br from-yellow-400/20 via-amber-500/20 to-orange-500/20 border-amber-500/50 overflow-hidden">
            <CardHeader className="text-center">
                <Trophy className="w-12 h-12 mx-auto text-amber-500" />
                <CardTitle className="text-2xl font-bold">Reward Unlocked!</CardTitle>
                <CardDescription className="text-base">Congratulations on your {rewardStreakGoal}-day streak!</CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
                 <div className="flex justify-center">
                     <Image src="/icons/Smash-Burger.jpg" alt="Smashhh Burgers Logo" width={100} height={100} className="rounded-full shadow-md" data-ai-hint="burger logo"/>
                </div>
                <p className="text-muted-foreground">As a reward for your dedication, our founding partner, <strong className="text-foreground">Smashhh Burgers</strong>, is offering you a special deal!</p>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button size="lg" className="w-full bg-amber-500 hover:bg-amber-600 text-black">
                            <Gift className="mr-2 h-5 w-5" />
                            Claim Your Reward
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Claim Your One-Time Reward?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will reveal a unique, one-time use discount code for your reward at Smashhh Burgers. It will expire in 7 days. Are you ready to redeem it?
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Not Now</AlertDialogCancel>
                            <AlertDialogAction onClick={handleClaim} disabled={loading} className="bg-amber-500 hover:bg-amber-600 text-black">
                                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Yes, Claim It!'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </CardContent>
        </Card>
    );
};

const RewardsProgressView = ({ streak, rewardClaimedAt, onClaimReward }: { streak: number, rewardClaimedAt: Timestamp | null, onClaimReward: () => Promise<void> }) => {
    const REWARD_STREAK_GOAL = 30;
    const progress = Math.min((streak / REWARD_STREAK_GOAL) * 100, 100);
    const showClaimableReward = streak >= REWARD_STREAK_GOAL;

    return (
        <div className="p-4 space-y-6">
            {showClaimableReward ? (
                <RewardCard onClaim={onClaimReward} rewardClaimedAt={rewardClaimedAt} rewardStreakGoal={REWARD_STREAK_GOAL} />
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-3">
                            <Image src="/icons/Smash-Burger.jpg" alt="Smashhh Burgers Logo" width={40} height={40} className="rounded-full" data-ai-hint="burger logo"/>
                            Smashhh Burgers Reward
                        </CardTitle>
                        <CardDescription>
                            Maintain a {REWARD_STREAK_GOAL}-day streak to unlock an exclusive reward.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <p className="font-semibold text-primary">{streak} / {REWARD_STREAK_GOAL} Day Streak</p>
                                <p className="text-sm font-bold">{Math.round(progress)}%</p>
                            </div>
                            <Progress value={progress} />
                        </div>
                        <p className="text-sm text-muted-foreground text-center">
                            Keep up the great work! You're {REWARD_STREAK_GOAL - streak} days away from a delicious reward.
                        </p>
                    </CardContent>
                </Card>
            )}
            
            <Card>
                <CardHeader>
                    <CardTitle>More Rewards Coming Soon!</CardTitle>
                    <CardDescription>We're partnering with more businesses to bring you exciting new rewards.</CardDescription>
                </CardHeader>
            </Card>
        </div>
    );
}

export default function HabitTrackerView() {
    const { toast } = useToast();
    const router = useRouter();
    const { user, isPremium } = useAuth();
    
    const [view, setView] = React.useState<'habits' | 'rewards'>('habits');
    const [habits, setHabits] = React.useState<Habit[]>([]);
    const [habitLogs, setHabitLogs] = React.useState<HabitLog[]>([]);
    const [streakData, setStreakData] = React.useState<UserHabitData>({ streak: 0, lastCompletionDate: null, rewardClaimedAt: null });
    const [loading, setLoading] = React.useState(true);

    const createHabitForm = useForm<z.infer<typeof createHabitSchema>>({
        resolver: zodResolver(createHabitSchema),
        defaultValues: { name: "", description: "" },
    });
    
    React.useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }

        setLoading(true);

        const userHabitsRef = doc(db, 'userHabits', user.uid);
        const habitsCollectionRef = collection(db, 'userHabits', user.uid, 'habits');

        const initializeUser = async () => {
            try {
                const userDocSnap = await getDoc(userHabitsRef);
                const batch = writeBatch(db);

                if (!userDocSnap.exists()) {
                    batch.set(userHabitsRef, { streak: 0, lastCompletionDate: null, logs: [], rewardClaimedAt: null });
                    predefinedHabits.forEach(h => {
                        const newHabitRef = doc(habitsCollectionRef);
                        batch.set(newHabitRef, { ...h, completed: false, isCustom: false });
                    });
                    await batch.commit();
                } else {
                    const data = userDocSnap.data() as UserHabitData;
                    const today = new Date().toISOString().split('T')[0];
                    const lastDate = data.lastCompletionDate;

                    if (lastDate && lastDate !== today) {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        const yesterdayStr = yesterday.toISOString().split('T')[0];
                        const newStreak = (lastDate === yesterdayStr) ? data.streak : 0;

                        if (newStreak === 0 && data.streak > 0) {
                            toast({ title: "Streak Reset", description: "You missed a day! Your streak has been reset.", variant: "destructive" });
                        }
                        
                        batch.update(userHabitsRef, { streak: newStreak });
                        
                        const habitsSnapshot = await getDocs(habitsCollectionRef);
                        habitsSnapshot.forEach(habitDoc => {
                            if (habitDoc.data().completed) {
                                batch.update(habitDoc.ref, { completed: false });
                            }
                        });

                        await batch.commit();
                    }
                }
            } catch (error) {
                console.error("Error initializing user habits:", error);
                // Don't show a toast here, as it might be a permission issue during initial load.
            }
        };

        const setupListeners = () => {
            const unsubscribeUser = onSnapshot(userHabitsRef, (userDoc) => {
                 const data = userDoc.data() as UserHabitData;
                 if (data) {
                    setStreakData({
                        streak: data.streak || 0,
                        lastCompletionDate: data.lastCompletionDate || null,
                        rewardClaimedAt: data.rewardClaimedAt || null,
                    });
                    setHabitLogs(data.logs || []);
                 }
                 setLoading(false); // Stop loading once we get data (or confirm it doesn't exist)
            }, (error) => {
                console.error("Error listening to user habits:", error);
                setLoading(false); // Stop loading on error too
            });

            const unsubscribeHabits = onSnapshot(query(habitsCollectionRef), (snapshot) => {
                const userHabits = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Habit));
                setHabits(userHabits);
            }, (error) => {
                console.error("Error listening to habits collection:", error);
            });

            return () => {
                unsubscribeUser();
                unsubscribeHabits();
            };
        }

        initializeUser().then(setupListeners);

    }, [user, toast]);

    const handleHabitToggle = async (habitId: string) => {
        if (!user || loading) return;

        const currentHabit = habits.find(h => h.id === habitId);
        if (!currentHabit) return;

        const newCompletedStatus = !currentHabit.completed;

        const updatedHabits = habits.map(h => h.id === habitId ? { ...h, completed: newCompletedStatus } : h);
        setHabits(updatedHabits);

        try {
            const habitRef = doc(db, 'userHabits', user.uid, 'habits', habitId);
            const userHabitsRef = doc(db, 'userHabits', user.uid);
            
            const batch = writeBatch(db);
            batch.update(habitRef, { completed: newCompletedStatus });

            const habitsAfterToggle = updatedHabits;
            const allCompleted = habitsAfterToggle.every(h => h.completed);
            const today = new Date().toISOString().split('T')[0];

            if (allCompleted && streakData.lastCompletionDate !== today) {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = yesterday.toISOString().split('T')[0];
                
                const newStreak = (streakData.lastCompletionDate === yesterdayStr) ? (streakData.streak || 0) + 1 : 1;

                const currentLogs = (await getDoc(userHabitsRef)).data()?.logs || [];
                const newLogs = currentLogs.filter((l: HabitLog) => l.date !== today);
                newLogs.push({ date: today, completedCount: habitsAfterToggle.length, totalCount: habitsAfterToggle.length });
                
                batch.update(userHabitsRef, { streak: newStreak, lastCompletionDate: today, logs: newLogs });

                 toast({
                    title: "Day Complete!",
                    description: `Great job! You've extended your streak to ${newStreak} days.`,
                });
            }
            
            await batch.commit();

        } catch (error) {
            console.error("Error updating habit:", error);
            toast({ title: "Error", description: "Could not update habit status.", variant: "destructive" });
            setHabits(habits); 
        }
    };

    const handleCreateHabit = async (values: z.infer<typeof createHabitSchema>) => {
        if (!user) return;

        const newHabitData = {
            name: values.name,
            description: values.description || 'Custom habit',
            completed: false,
            isCustom: true,
        };
        const habitsCollectionRef = collection(db, 'userHabits', user.uid, 'habits');
        await addDoc(habitsCollectionRef, newHabitData);
        
        toast({ title: "Habit Created!", description: `"${values.name}" has been added to your list.`});
        createHabitForm.reset();
        document.getElementById('close-create-habit-dialog')?.click();
    };

    const handleDeleteHabit = async (habitId: string) => {
        if (!user) return;
        await deleteDoc(doc(db, 'userHabits', user.uid, 'habits', habitId));
        toast({ title: "Habit Removed", description: "The custom habit has been deleted." });
    };
    
    const handleUpgradeClick = (plan: 'monthly' | 'yearly') => {
        if (!user) {
            toast({ title: "Authentication Error", description: "You must be signed in to upgrade.", variant: "destructive" });
            return;
        }
        router.push(`/checkout?plan=${plan}`);
    };

    const handleClaimReward = async () => {
        if (!user) return;
        try {
            const userHabitsRef = doc(db, 'userHabits', user.uid);
            await updateDoc(userHabitsRef, { rewardClaimedAt: serverTimestamp() });
            toast({
                title: "Reward Claimed!",
                description: "Your reward discount code is now visible. It expires in 7 days!",
            });
        } catch (error) {
            console.error("Error claiming reward:", error);
            toast({ title: "Error", description: "Could not claim your reward. Please try again.", variant: "destructive" });
        }
    };
    
    if (loading) return <div className="flex justify-center items-center h-full"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    if (!user) return <AuthGate />;

    const completedCount = habits.filter(h => h.completed).length;
    const isTodayCompleted = habits.length > 0 && completedCount === habits.length;

    const HabitsListView = () => (
        <div className="p-4 space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Daily Motivation</CardTitle>
                </CardHeader>
                <CardContent>
                    <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                        "The most beloved of deeds to Allah are those that are most consistent, even if they are few."
                        <cite className="block text-right not-italic text-sm font-semibold mt-2">- Prophet Muhammad (ﷺ)</cite>
                    </blockquote>
                </CardContent>
            </Card>

            {isPremium && (
                <>
                    <HabitDashboard habitLogs={habitLogs} />
                    <ThemeSelector />
                </>
            )}
            
            <Card>
                <CardHeader>
                    <CardTitle>Your Daily Habits</CardTitle>
                     {isTodayCompleted ? (
                        <CardDescription className="text-primary font-semibold flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            All habits completed for today. See you tomorrow!
                        </CardDescription>
                    ) : (
                        <CardDescription>Check off your habits for today.</CardDescription>
                    )}
                </CardHeader>
                <CardContent className="space-y-4">
                    {habits.map(habit => (
                        <div key={habit.id} 
                             className={cn("flex items-center space-x-3 p-3 rounded-lg border transition-colors",
                                habit.completed ? 'bg-primary/10 border-primary/20' : 'bg-background',
                                isTodayCompleted && 'cursor-not-allowed opacity-70'
                             )}
                        >
                            <Checkbox 
                                id={habit.id} 
                                checked={habit.completed}
                                onCheckedChange={() => handleHabitToggle(habit.id)}
                                disabled={isTodayCompleted}
                            />
                            <div className="grid gap-1.5 leading-none w-full cursor-pointer" onClick={() => !isTodayCompleted && handleHabitToggle(habit.id)}>
                                <Label htmlFor={habit.id} className={cn("text-base font-medium", 
                                    isTodayCompleted ? 'cursor-not-allowed' : 'cursor-pointer',
                                    habit.completed && 'line-through text-muted-foreground'
                                )}>
                                    {habit.name}
                                </Label>
                                <p className="text-sm text-muted-foreground">
                                    {habit.description}
                                </p>
                            </div>
                            {habit.isCustom && (
                                 <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" onClick={() => handleDeleteHabit(habit.id)}>
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    ))}
                     {isPremium && (
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="w-full mt-4">
                                    <PlusCircle className="mr-2 h-4 w-4" />
                                    Create Custom Habit
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Create a New Habit</DialogTitle>
                                    <DialogDescription>
                                        Add a personal habit to your daily tracking list.
                                    </DialogDescription>
                                </DialogHeader>
                                <Form {...createHabitForm}>
                                    <form onSubmit={createHabitForm.handleSubmit(handleCreateHabit)} className="space-y-4">
                                        <FormField
                                            control={createHabitForm.control}
                                            name="name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Habit Name</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="e.g., Read Quran for 15 mins" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={createHabitForm.control}
                                            name="description"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Description (Optional)</FormLabel>
                                                    <FormControl>
                                                        <Textarea placeholder="e.g., After Fajr prayer" {...field} />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                         <DialogFooter>
                                            <DialogClose asChild>
                                                <Button type="button" variant="secondary" id="close-create-habit-dialog">Cancel</Button>
                                            </DialogClose>
                                            <Button type="submit">Create Habit</Button>
                                         </DialogFooter>
                                    </form>
                                </Form>
                            </DialogContent>
                        </Dialog>
                    )}
                </CardContent>
                 <CardFooter className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>🔥 Streak: {streakData.streak || 0} days</span>
                    <span>✅ Completed: {completedCount}/{habits.length}</span>
                </CardFooter>
            </Card>

            {!isPremium && (
            <Card className="bg-gradient-to-br from-primary/20 to-background border-primary/50 overflow-hidden">
                <CardHeader>
                     <div className="flex items-center gap-2">
                        <Star className="w-6 h-6 text-yellow-500" />
                        <CardTitle>Unlock Your Full Potential</CardTitle>
                    </div>
                    <CardDescription>Go premium to access exclusive features and build unlimited habits.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <ul className="space-y-4">
                        <PremiumFeature icon={Zap} title="Unlimited Habits" description="Track fasting, Tahajjud, avoiding backbiting, and more." />
                        <PremiumFeature icon={PlusCircle} title="Custom Habit Creation" description="Create personalized habits like 'Call my parents on Friday'." />
                        <PremiumFeature icon={BarChart3} title="Visual Dashboards" description="Get detailed analytics on your progress and consistency." />
                        <PremiumFeature icon={Clock} title="Reminders & Widgets" description="Stay on track with notifications and home screen widgets." comingSoon={true} />
                        <PremiumFeature icon={Gift} title="Themed UI & Rewards" description="Customize your experience and earn rewards for your dedication." comingSoon={true} />
                    </ul>
                </CardContent>
                <CardFooter className="bg-background/50 p-4 flex-col sm:flex-row gap-2">
                    <Button className="w-full" size="lg" onClick={() => handleUpgradeClick('monthly')}>
                        <Lock className="mr-2 h-4 w-4" />
                        Upgrade Now - $1.99/month
                    </Button>
                     <div className="flex gap-2 w-full sm:w-auto">
                        <Button className="flex-1" variant="secondary" onClick={() => handleUpgradeClick('yearly')}>Yearly ($15)</Button>
                    </div>
                </CardFooter>
            </Card>
            )}
        </div>
    );

    return (
        <div className="flex flex-col h-full bg-muted/20">
            <header className="p-4 border-b sticky top-0 bg-background z-10 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    {view === 'rewards' && (
                        <Button variant="ghost" size="icon" className="mr-2" onClick={() => setView('habits')}>
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                    )}
                    <div className="flex-shrink-0 bg-primary/20 text-primary rounded-full p-3">
                         {view === 'habits' ? <Target className="w-6 h-6" /> : <Trophy className="w-6 h-6" />}
                    </div>
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold text-foreground">
                            {view === 'habits' ? 'Habit Tracker' : 'Rewards'}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                             {view === 'habits' ? 'Build and maintain your deen.' : 'Track your progress towards rewards.'}
                        </p>
                    </div>
                </div>
                {view === 'habits' && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setView('rewards')}
                        className="group"
                    >
                        <Gift className="mr-2 h-4 w-4 text-amber-500" />
                        Rewards
                    </Button>
                )}
            </header>
            <ScrollArea className="flex-1">
                {view === 'habits' ? <HabitsListView /> : <RewardsProgressView streak={streakData.streak} rewardClaimedAt={streakData.rewardClaimedAt} onClaimReward={handleClaimReward} />}
            </ScrollArea>
        </div>
    );
}
